import React from 'react';
import { Share, PlusSquare, Smartphone, Check, Info, X, Download } from 'lucide-react';

/**
 * PWA install help modal — split out of PWAInstallBanner.jsx so that both the
 * first-visit banner AND the persistent "Install App" menu items can render
 * the exact same instructions without duplicating copy.
 *
 * Variants:
 *   - 'ios'             — iOS Safari (Share → Add to Home Screen)
 *   - 'android'         — Android Chrome (⋮ menu → Install app)
 *   - 'desktop'         — Chrome/Edge desktop (address-bar install icon)
 *   - 'in-app-browser'  — User opened the link inside WhatsApp / Facebook /
 *                          Instagram / Gmail / Twitter — there is no install
 *                          path here; we tell them to re-open in Chrome/Safari.
 */
const Step = ({ n, icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
      {n}
    </span>
    <div className="flex-1 min-w-0 text-sm text-gray-700 leading-snug">
      <span className="inline-flex items-center gap-2 align-middle">
        {children}
        <span className="inline-flex">{icon}</span>
      </span>
    </div>
  </li>
);

const PWAInstallHelpModal = ({ variant = 'desktop', t, onClose }) => {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      data-testid="pwa-help-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-white" />
            <h2 className="text-white font-semibold text-base" data-testid={`pwa-help-title-${variant}`}>
              {variant === 'ios' && t('pwaHelpIOSTitle', 'Install on iPhone')}
              {variant === 'android' && t('pwaHelpAndroidTitle', 'Install on Android')}
              {variant === 'desktop' && t('pwaHelpDesktopTitle', 'Install Find-2-Day')}
              {variant === 'in-app-browser' && t('pwaHelpInAppTitle', 'Open in your browser first')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 -mr-1"
            data-testid="pwa-help-close-btn"
            aria-label={t('close', 'Close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {variant === 'ios' && (
            <>
              <p className="text-sm text-gray-600">{t('pwaHelpIOSIntro', "iPhones add web apps to the Home Screen from Safari's share menu. Just follow these three steps:")}</p>
              <ol className="space-y-3">
                <Step n="1" icon={<Share className="h-5 w-5 text-blue-500" />}>
                  {t('pwaHelpIOSStep1', 'Tap the Share button at the bottom of Safari (the square with an arrow pointing up).')}
                </Step>
                <Step n="2" icon={<PlusSquare className="h-5 w-5 text-gray-700" />}>
                  {t('pwaHelpIOSStep2', 'Scroll down in the share sheet and tap "Add to Home Screen".')}
                </Step>
                <Step n="3" icon={<Check className="h-5 w-5 text-emerald-600" />}>
                  {t('pwaHelpIOSStep3', 'Tap "Add" in the top-right corner. Find-2-Day will now appear on your Home Screen like a regular app.')}
                </Step>
              </ol>
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t('pwaHelpIOSNote', 'Add to Home Screen only works inside Safari. If you are using Chrome or another browser on iPhone, please open this page in Safari first.')}</span>
              </div>
            </>
          )}
          {variant === 'android' && (
            <>
              <p className="text-sm text-gray-600">{t('pwaHelpAndroidIntro', 'If the install prompt did not appear automatically, you can install Find-2-Day from your browser menu:')}</p>
              <ol className="space-y-3">
                <Step n="1" icon={<span className="text-gray-700 font-bold">⋮</span>}>
                  {t('pwaHelpAndroidStep1', 'Tap the menu button (three dots) in the top-right of Chrome.')}
                </Step>
                <Step n="2" icon={<Download className="h-5 w-5 text-purple-600" />}>
                  {t('pwaHelpAndroidStep2', 'Choose "Install app" or "Add to Home screen" from the menu.')}
                </Step>
                <Step n="3" icon={<Check className="h-5 w-5 text-emerald-600" />}>
                  {t('pwaHelpAndroidStep3', 'Confirm to add the icon to your Home Screen.')}
                </Step>
              </ol>
            </>
          )}
          {variant === 'desktop' && (
            <>
              <p className="text-sm text-gray-600">{t('pwaHelpDesktopIntro', 'Installing Find-2-Day is fastest on a phone, but you can also install it on your computer with Chrome or Edge:')}</p>
              <ol className="space-y-3">
                <Step n="1" icon={<Download className="h-5 w-5 text-purple-600" />}>
                  {t('pwaHelpDesktopStep1', 'Look for the install icon in the address bar (a small monitor with a down-arrow) or open the browser menu.')}
                </Step>
                <Step n="2" icon={<Check className="h-5 w-5 text-emerald-600" />}>
                  {t('pwaHelpDesktopStep2', 'Choose "Install Find-2-Day" — the site will open as its own desktop window.')}
                </Step>
              </ol>
              <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t('pwaHelpDesktopNote', 'Firefox and desktop Safari do not currently support installable web apps. For the full app experience, open this page on your phone.')}</span>
              </div>
            </>
          )}
          {variant === 'in-app-browser' && (
            <>
              <p className="text-sm text-gray-600">
                {t('pwaHelpInAppIntro', "You're viewing Find-2-Day inside another app's built-in browser (WhatsApp, Facebook, Instagram, Gmail, etc.). These in-app browsers cannot install web apps. To install Find-2-Day:")}
              </p>
              <ol className="space-y-3">
                <Step n="1" icon={<span className="text-gray-700 font-bold">⋮</span>}>
                  {t('pwaHelpInAppStep1', 'Tap the menu button in this browser (usually ⋮ or … in the corner).')}
                </Step>
                <Step n="2" icon={<Download className="h-5 w-5 text-purple-600" />}>
                  {t('pwaHelpInAppStep2', 'Choose "Open in Chrome" (Android) or "Open in Safari" (iPhone).')}
                </Step>
                <Step n="3" icon={<Check className="h-5 w-5 text-emerald-600" />}>
                  {t('pwaHelpInAppStep3', 'Once Find-2-Day reopens in Chrome or Safari, tap "Install App" again from the Profile page or browser menu.')}
                </Step>
              </ol>
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t('pwaHelpInAppNote', 'This is a limitation of in-app browsers, not Find-2-Day. The same applies to every installable web app.')}</span>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 active:bg-purple-800 transition-colors"
            data-testid="pwa-help-got-it-btn"
          >
            {t('pwaHelpGotIt', 'Got it')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallHelpModal;
